import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { User, Shield, Bell, Trash2, Save, Loader2, SlidersHorizontal, ChevronRight, Crown, CreditCard, ExternalLink, Camera } from "lucide-react";
import { useSubscription } from "@/hooks/use-subscription";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { OnboardingWizard, OnboardingData } from "@/components/onboarding/OnboardingWizard";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export default function SettingsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { subscribed: isPremium, plan_name, subscription_end, cancel_at_period_end, loading: subLoading } = useSubscription();
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [emailNotifications, setEmailNotifications] = useState(true);
  const [savedPreferences, setSavedPreferences] = useState<OnboardingData | null>(null);
  const [showPreferencesWizard, setShowPreferencesWizard] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Load user profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading profile:', error);
          return;
        }

        if (profile) {
          profileForm.setValue('firstName', profile.first_name || '');
          profileForm.setValue('lastName', profile.last_name || '');
          setAvatarUrl((profile as any).avatar_url || null);
          
          if (profile.saved_preferences) {
            setSavedPreferences(profile.saved_preferences as unknown as OnboardingData);
          }
        }

        profileForm.setValue('email', user.email || '');
        
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    if (user) {
      loadProfile();
    }
  }, [user, profileForm]);

  const onSubmitProfile = async (data: ProfileFormData) => {
    if (!user) return;

    setIsLoadingProfile(true);
    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          first_name: data.firstName,
          last_name: data.lastName,
        });

      if (profileError) throw profileError;

      // Update email if changed
      if (data.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: data.email,
        });

        if (emailError) throw emailError;

        toast.success("Profile updated successfully", {
          description: "Please check your new email to confirm the change.",
        });
      } else {
        toast.success("Profile updated successfully");
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error("Failed to update profile", {
        description: error.message || "Please try again.",
      });
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    if (!user) return;

    setIsLoadingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword,
      });

      if (error) throw error;

      toast.success("Password updated successfully");
      passwordForm.reset();
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast.error("Failed to update password", {
        description: error.message || "Please try again.",
      });
    } finally {
      setIsLoadingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setIsLoadingDelete(true);
    try {
      // Call edge function to handle account deletion
      const { error } = await supabase.functions.invoke('delete-user-account', {
        body: { userId: user.id }
      });

      if (error) throw error;

      toast.success("Account deleted successfully");
      
      // Sign out the user
      await supabase.auth.signOut();
    } catch (error: any) {
      console.error('Error deleting account:', error);
      toast.error("Failed to delete account", {
        description: error.message || "Please contact support for assistance.",
      });
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const handleSavePreferences = async (data: OnboardingData) => {
    if (!user) return;
    setIsSavingPreferences(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          saved_preferences: {
            careNeeds: data.careNeeds,
            timeframe: data.timeframe,
            counties: data.counties,
            priorities: data.priorities,
            onboarding_completed_at: new Date().toISOString(),
          },
        })
        .eq("id", user.id);
      if (error) throw error;
      setSavedPreferences(data);
      setShowPreferencesWizard(false);
      toast.success("Care preferences saved!");
    } catch (err: any) {
      toast.error("Failed to save preferences", { description: err.message });
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("Image too large", { description: "Please choose an image under 5MB." });
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Add cache-busting so browser reloads the new image
      const urlWithBust = `${publicUrl}?t=${Date.now()}`;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ avatar_url: urlWithBust } as any)
        .eq('id', user.id);

      if (profileError) throw profileError;

      setAvatarUrl(urlWithBust);
      toast.success("Profile photo updated!");
    } catch (err: any) {
      toast.error("Failed to upload photo", { description: err.message });
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user) return;
    setIsUploadingAvatar(true);
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ avatar_url: null } as any)
        .eq('id', user.id);

      if (profileError) throw profileError;
      setAvatarUrl(null);
      toast.success("Profile photo removed.");
    } catch (err: any) {
      toast.error("Failed to remove photo", { description: err.message });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth/login?redirectTo=/settings" />;
  }

  return (
    <Layout>
      <Helmet>
        <title>Account Settings | Grandpal</title>
      </Helmet>

      <div className="container py-8 max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-grandpal-green-dark mb-2">Account Settings</h1>
          <p className="text-gray-600">Manage your account preferences and security settings</p>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
            </CardHeader>
        <CardContent>
              {/* Avatar upload */}
              <div className="flex items-center gap-5 mb-6 pb-6 border-b border-border">
                <div className="relative group">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="Profile photo"
                      className="w-20 h-20 rounded-full object-cover border-2 border-border"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold border-2 border-border">
                      {(profileForm.watch('firstName')?.charAt(0) || user?.email?.charAt(0) || '?').toUpperCase()}
                    </div>
                  )}
                  {isUploadingAvatar && (
                    <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Profile Photo</p>
                  <p className="text-xs text-muted-foreground">JPG, PNG or WebP · Max 5MB</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                      className="flex items-center gap-1.5"
                    >
                      <Camera className="h-3.5 w-3.5" />
                      {avatarUrl ? "Change photo" : "Upload photo"}
                    </Button>
                    {avatarUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveAvatar}
                        disabled={isUploadingAvatar}
                        className="text-destructive hover:text-destructive"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
              </div>

              <form onSubmit={profileForm.handleSubmit(onSubmitProfile)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      {...profileForm.register('firstName')}
                      className="mt-1"
                    />
                    {profileForm.formState.errors.firstName && (
                      <p className="text-sm text-red-600 mt-1">
                        {profileForm.formState.errors.firstName.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      {...profileForm.register('lastName')}
                      className="mt-1"
                    />
                    {profileForm.formState.errors.lastName && (
                      <p className="text-sm text-red-600 mt-1">
                        {profileForm.formState.errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    {...profileForm.register('email')}
                    className="mt-1"
                  />
                  {profileForm.formState.errors.email && (
                    <p className="text-sm text-red-600 mt-1">
                      {profileForm.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={isLoadingProfile}
                  className="bg-grandpal-green hover:bg-grandpal-green/90"
                >
                  {isLoadingProfile ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Password & Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Password & Security
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    {...passwordForm.register('currentPassword')}
                    className="mt-1"
                  />
                  {passwordForm.formState.errors.currentPassword && (
                    <p className="text-sm text-red-600 mt-1">
                      {passwordForm.formState.errors.currentPassword.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      {...passwordForm.register('newPassword')}
                      className="mt-1"
                    />
                    {passwordForm.formState.errors.newPassword && (
                      <p className="text-sm text-red-600 mt-1">
                        {passwordForm.formState.errors.newPassword.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      {...passwordForm.register('confirmPassword')}
                      className="mt-1"
                    />
                    {passwordForm.formState.errors.confirmPassword && (
                      <p className="text-sm text-red-600 mt-1">
                        {passwordForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoadingPassword}
                  className="bg-grandpal-green hover:bg-grandpal-green/90"
                >
                  {isLoadingPassword ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="h-4 w-4 mr-2" />
                  )}
                  Update Password
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-gray-600">
                    Receive updates about nursing home availability and waitlist status
                  </p>
                </div>
                <Switch
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                />
              </div>
            </CardContent>
          </Card>

          {/* Care Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5" />
                Care Preferences
              </CardTitle>
            </CardHeader>
            <CardContent>
              {showPreferencesWizard ? (
                <div className="mt-2">
                  <OnboardingWizard
                    firstName={user?.user_metadata?.first_name}
                    onComplete={handleSavePreferences}
                    onSkip={() => setShowPreferencesWizard(false)}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {savedPreferences ? (
                    <div className="space-y-3 text-sm text-muted-foreground">
                      {savedPreferences.careNeeds?.length > 0 && (
                        <div>
                          <span className="font-medium text-foreground">Care needs: </span>
                          {savedPreferences.careNeeds.join(", ")}
                        </div>
                      )}
                      {savedPreferences.timeframe && (
                        <div>
                          <span className="font-medium text-foreground">Timeframe: </span>
                          {savedPreferences.timeframe}
                        </div>
                      )}
                      {savedPreferences.counties?.length > 0 && (
                        <div>
                          <span className="font-medium text-foreground">Preferred counties: </span>
                          {savedPreferences.counties.join(", ")}
                        </div>
                      )}
                      {savedPreferences.priorities?.length > 0 && (
                        <div>
                          <span className="font-medium text-foreground">Priorities: </span>
                          {savedPreferences.priorities.join(", ")}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      You haven't set your care preferences yet. Tell us what you're looking for to get personalised recommendations.
                    </p>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setShowPreferencesWizard(true)}
                    className="flex items-center gap-2"
                  >
                    <ChevronRight className="h-4 w-4" />
                    {savedPreferences ? "Update preferences" : "Set preferences"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Subscription */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5" />
                Subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Loading subscription info…</span>
                </div>
              ) : isPremium ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold bg-primary/10 text-primary border border-primary/20">
                      <Crown className="h-3.5 w-3.5" />
                      {plan_name || "Premium"}
                    </span>
                    <span className="text-sm text-muted-foreground">Active</span>
                  </div>

                  {subscription_end && (
                    <div className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {cancel_at_period_end ? "Cancels on: " : "Renews on: "}
                      </span>
                      {new Date(subscription_end).toLocaleDateString("en-IE", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  )}

                  {cancel_at_period_end && (
                    <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                      <svg className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <div className="text-sm">
                        <p className="font-medium">Your subscription is set to cancel</p>
                        <p className="text-amber-700 mt-0.5">
                          You'll keep access to Premium features until{" "}
                          {subscription_end
                            ? new Date(subscription_end).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })
                            : "your billing period ends"}
                          . Reactivate anytime via Manage subscription.
                        </p>
                      </div>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={async () => {
                      try {
                        const { data, error } = await supabase.functions.invoke("customer-portal");
                        if (error) throw error;
                        if (data?.url) window.open(data.url, "_blank");
                      } catch (err: any) {
                        toast.error("Could not open billing portal", { description: err.message });
                      }
                    }}
                  >
                    <CreditCard className="h-4 w-4" />
                    Manage subscription
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    You're on the free plan. Upgrade to Premium to unlock priority support, advanced analytics, and more.
                  </p>
                  <Button asChild className="bg-grandpal-green hover:bg-grandpal-green/90">
                    <a href="/premium" className="flex items-center gap-2">
                      <Crown className="h-4 w-4" />
                      Upgrade to Premium
                    </a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-red-600 mb-2">Delete Account</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete your account
                          and remove all your data from our servers, including:
                          <ul className="mt-2 ml-4 list-disc">
                            <li>Your profile information</li>
                            <li>Your nursing home leads and waitlist positions</li>
                            <li>Your saved preferences</li>
                          </ul>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteAccount}
                          disabled={isLoadingDelete}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {isLoadingDelete && (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          )}
                          Yes, delete my account
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}